import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Folder } from "lucide-react";
import toast from "react-hot-toast";
import { useUser } from "@/components/UserProvider";
import { AppModal, Button, RadioBoxInput } from "@/components/ui";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/shadcn/Breadcrumb/Breadcrumb";
import { automationError } from "@/components/features/automations/automationError";
import { fileDuplicationDestinationOptions } from "@/lib/data/react-query/files/file-duplication-destinations";
import { useCreateFileDuplication } from "@/lib/data/react-query/files/file-duplication";
import type { FileDuplicationCreate } from "@/lib/data/schemas/file-duplication";
import { createFileDuplication } from "@/lib/data/requests/files/file-duplication";
import type { FileDestination } from "@/lib/data/schemas/files";
import { TrackedDuplication } from "./TrackedDuplication";
import { PendingDuplicationToast } from "./PendingDuplicationToast";

type Repository =
  | { scope: "personal" | "firm" }
  | { scope: "matter"; matterId: string }
  | { scope: "mass_tort"; massTortId: string };

export type FileDuplicationSelection = {
  files: { id: string; name: string; isFolder: boolean }[];
  repository: Repository;
  label: string;
};

export type TrackedOperation = { id: string; label: string };
export type PendingDuplication = {
  request: FileDuplicationCreate;
  label: string;
};

function isDefinitiveRejection(error: unknown) {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}
type DuplicationContextValue = {
  openDuplication: (selection: FileDuplicationSelection) => void;
};

interface DuplicationProviderHmrData {
  duplicationContext?: React.Context<DuplicationContextValue | null>;
}

const duplicationProviderHmrData = import.meta.hot?.data as
  | DuplicationProviderHmrData
  | undefined;
const DuplicationContext =
  duplicationProviderHmrData?.duplicationContext ??
  createContext<DuplicationContextValue | null>(null);

if (import.meta.hot) {
  const hotData = import.meta.hot.data as
    | DuplicationProviderHmrData
    | undefined;
  if (hotData) hotData.duplicationContext = DuplicationContext;
}

export function useFileDuplication() {
  const context = useContext(DuplicationContext);
  if (!context) throw new Error("File duplication is unavailable.");
  return context;
}

function storedOperations(key: string): TrackedOperation[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(value)) return [];
    const entries: unknown[] = value;
    return entries.filter(
      (entry): entry is TrackedOperation =>
        typeof entry === "object" &&
        entry !== null &&
        "id" in entry &&
        typeof entry.id === "string" &&
        "label" in entry &&
        typeof entry.label === "string",
    );
  } catch {
    return [];
  }
}

function storedPending(key: string): PendingDuplication[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(value)) return [];
    const entries: unknown[] = value;
    return entries.filter(
      (entry): entry is PendingDuplication =>
        typeof entry === "object" &&
        entry !== null &&
        "label" in entry &&
        typeof entry.label === "string" &&
        "request" in entry &&
        typeof entry.request === "object" &&
        entry.request !== null &&
        "requestId" in entry.request &&
        typeof entry.request.requestId === "string" &&
        "fileIds" in entry.request &&
        Array.isArray(entry.request.fileIds) &&
        entry.request.fileIds.every((id: unknown) => typeof id === "string") &&
        "mode" in entry.request &&
        (entry.request.mode === "folders_and_files" ||
          entry.request.mode === "folders_only") &&
        "destinationFolderId" in entry.request &&
        (entry.request.destinationFolderId === null ||
          typeof entry.request.destinationFolderId === "string"),
    );
  } catch {
    return [];
  }
}

export function FileDuplicationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { currentUser } = useUser();
  const storageKey = `file-duplications:${currentUser.firmId}:${currentUser.id}`;
  return (
    <FileDuplicationSession key={storageKey} storageKey={storageKey}>
      {children}
    </FileDuplicationSession>
  );
}

function FileDuplicationSession({
  children,
  storageKey,
}: {
  children: React.ReactNode;
  storageKey: string;
}) {
  const [selection, setSelection] = useState<FileDuplicationSelection | null>(
    null,
  );
  const [operations, setOperations] = useState<TrackedOperation[]>(() =>
    storedOperations(storageKey),
  );
  const pendingKey = `${storageKey}:pending`;
  const [pending, setPending] = useState<PendingDuplication[]>(() =>
    storedPending(pendingKey),
  );
  const pendingRef = useRef(pending);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [attemptingId, setAttemptingId] = useState<string | null>(null);
  const operationToastIds = useRef(new Set<string>());
  useEffect(() => {
    operations.forEach((operation) => {
      operationToastIds.current.add(`file-duplication-${operation.id}`);
    });
  }, [operations]);
  useEffect(
    () => () => {
      operationToastIds.current.forEach((id) => {
        toast.remove(id);
      });
    },
    [],
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(operations));
    } catch {
      // Progress remains available for this session if storage is unavailable.
    }
  }, [operations, storageKey]);

  const updatePending = (
    update: (previous: PendingDuplication[]) => PendingDuplication[],
  ) => {
    const next = update(pendingRef.current);
    pendingRef.current = next;
    try {
      localStorage.setItem(pendingKey, JSON.stringify(next));
    } catch {
      // Keep the request ID in memory if storage is unavailable.
    }
    setPending(next);
  };

  const onStarted = (operation: TrackedOperation, requestId?: string) => {
    if (requestId)
      updatePending((previous) =>
        previous.filter((item) => item.request.requestId !== requestId),
      );
    setOperations((previous) => [
      operation,
      ...previous.filter((item) => item.id !== operation.id),
    ]);
    setSelection(null);
  };

  const retryPending = async (item: PendingDuplication) => {
    setRetryingId(item.request.requestId);
    try {
      const operation = await createFileDuplication(item.request);
      onStarted(
        { id: operation.id, label: item.label },
        item.request.requestId,
      );
    } catch (error) {
      if (isDefinitiveRejection(error))
        updatePending((previous) =>
          previous.filter(
            (entry) => entry.request.requestId !== item.request.requestId,
          ),
        );
      toast.error(automationError(error));
    } finally {
      setRetryingId(null);
    }
  };

  const context = useMemo<DuplicationContextValue>(
    () => ({
      openDuplication: setSelection,
    }),
    [],
  );

  return (
    <DuplicationContext.Provider value={context}>
      {children}
      <FileDuplicationDialog
        selection={selection}
        pending={pending}
        onClose={() => {
          setSelection(null);
        }}
        onAttempt={(item) => {
          setAttemptingId(item.request.requestId);
          updatePending((previous) => [
            item,
            ...previous.filter(
              (existing) =>
                existing.request.requestId !== item.request.requestId,
            ),
          ]);
        }}
        onStarted={onStarted}
        onAttemptFinished={() => {
          setAttemptingId(null);
        }}
        onRejected={(requestId) => {
          updatePending((previous) =>
            previous.filter((item) => item.request.requestId !== requestId),
          );
        }}
      />
      {pending
        .filter((item) => item.request.requestId !== attemptingId)
        .map((item) => (
          <PendingDuplicationToast
            key={item.request.requestId}
            item={item}
            retrying={retryingId !== null}
            onRetry={() => void retryPending(item)}
          />
        ))}
      {operations.map((operation) => (
        <TrackedDuplication
          key={operation.id}
          operation={operation}
          onDismiss={() => {
            setOperations((previous) =>
              previous.filter((item) => item.id !== operation.id),
            );
          }}
        />
      ))}
    </DuplicationContext.Provider>
  );
}

function FileDuplicationDialog({
  selection,
  pending,
  onClose,
  onAttempt,
  onStarted,
  onRejected,
  onAttemptFinished,
}: {
  selection: FileDuplicationSelection | null;
  pending: PendingDuplication[];
  onClose: () => void;
  onAttempt: (item: PendingDuplication) => void;
  onStarted: (operation: TrackedOperation, requestId: string) => void;
  onRejected: (requestId: string) => void;
  onAttemptFinished: () => void;
}) {
  const [path, setPath] = useState<{ id: string; name: string }[]>([]);
  const [mode, setMode] = useState<"folders_and_files" | "folders_only">(
    "folders_and_files",
  );
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const mutation = useCreateFileDuplication();
  const destination: FileDestination | undefined = selection
    ? path.length > 0
      ? { parentId: path[path.length - 1].id }
      : selection.repository
    : undefined;
  const folders = useInfiniteQuery({
    ...fileDuplicationDestinationOptions(destination ?? { scope: "personal" }),
    enabled: !!selection,
  });

  useEffect(() => {
    setPath([]);
    setMode("folders_and_files");
    setRequestId(crypto.randomUUID());
    mutation.reset();
    // A new selection starts a new idempotent request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  if (!selection) return null;
  const selectedFolderIds = new Set(
    selection.files.filter((file) => file.isFolder).map((file) => file.id),
  );
  const visibleFolders =
    folders.data?.pages
      .flatMap((page) => page.data)
      .filter((file) => file.isFolder && !file.systemFolder && !file.skillId) ??
    [];
  const hasFolders = selectedFolderIds.size > 0;
  const destinationInsideSource = path.some((folder) =>
    selectedFolderIds.has(folder.id),
  );
  const pendingForSelection = pending.some(
    (item) =>
      item.request.fileIds.length === selection.files.length &&
      selection.files.every((file) => item.request.fileIds.includes(file.id)),
  );
  const navigateTo = (depth: number) => {
    setPath(path.slice(0, depth));
    setRequestId(crypto.randomUUID());
    mutation.reset();
  };
  const start = async () => {
    const request: FileDuplicationCreate = {
      fileIds: selection.files.map((file) => file.id),
      destinationFolderId: path.at(-1)?.id ?? null,
      mode,
      requestId,
    };
    onAttempt({ request, label: selection.label });
    try {
      const operation = await mutation.mutateAsync(request);
      onStarted({ id: operation.id, label: selection.label }, requestId);
    } catch (error) {
      if (isDefinitiveRejection(error)) onRejected(requestId);
      // Keep uncertain requests so retrying cannot create a second copy.
    } finally {
      onAttemptFinished();
    }
  };
  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Duplicate ${selection.files.length === 1 ? selection.files[0].name : `${selection.files.length} items`}`}
      size="xl"
      loading={mutation.isPending}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              mutation.isPending ||
              pendingForSelection ||
              folders.isPending ||
              !!folders.error ||
              destinationInsideSource ||
              (mode === "folders_only" && !hasFolders)
            }
            onClick={() => void start()}
          >
            Duplicate
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {hasFolders && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Include</p>
            <RadioBoxInput
              ariaLabel="Include in duplicate"
              className="sm:grid-cols-2"
              disabled={mutation.isPending}
              options={[
                { label: "Folders and files", value: "folders_and_files" },
                { label: "Folder structure only", value: "folders_only" },
              ]}
              value={mode}
              onValueChange={(value) => {
                setMode(value);
                setRequestId(crypto.randomUUID());
                mutation.reset();
              }}
            />
          </div>
        )}
        <div className="space-y-2">
          <p className="text-sm font-medium">Destination</p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                {path.length ? (
                  <BreadcrumbLink asChild>
                    <button
                      type="button"
                      onClick={() => {
                        navigateTo(0);
                      }}
                    >
                      Top level
                    </button>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>Top level</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {path.map((folder, index) => (
                <Fragment key={folder.id}>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {index === path.length - 1 ? (
                      <BreadcrumbPage>{folder.name}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <button
                          type="button"
                          onClick={() => {
                            navigateTo(index + 1);
                          }}
                        >
                          {folder.name}
                        </button>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="max-h-60 min-h-40 overflow-y-auto rounded-md border p-2">
            {folders.isPending && (
              <p className="text-muted-foreground p-2 text-sm">
                Loading folders…
              </p>
            )}
            {folders.error && (
              <p role="alert" className="text-destructive p-2 text-sm">
                {automationError(folders.error)}
              </p>
            )}
            {!folders.isPending &&
              !folders.error &&
              visibleFolders.length === 0 && (
                <p className="text-muted-foreground p-2 text-sm">
                  No folders here
                </p>
              )}
            {visibleFolders.map((folder) => (
              <Button
                key={folder.id}
                type="button"
                variant="ghost"
                disabled={selectedFolderIds.has(folder.id)}
                className="h-auto w-full justify-start gap-2 px-2 py-2 text-left font-normal"
                onClick={() => {
                  setPath([...path, { id: folder.id, name: folder.name }]);
                  setRequestId(crypto.randomUUID());
                  mutation.reset();
                }}
              >
                <Folder className="size-4" />
                {folder.name}
                {selectedFolderIds.has(folder.id) && (
                  <span className="ml-auto text-xs">Selected source</span>
                )}
              </Button>
            ))}
            {folders.hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                disabled={folders.isFetchingNextPage}
                onClick={() => void folders.fetchNextPage()}
              >
                Load more
              </Button>
            )}
          </div>
        </div>
        {mutation.error && (
          <p role="alert" className="text-destructive text-sm">
            {automationError(mutation.error)}
          </p>
        )}
        {pendingForSelection && !mutation.isPending && (
          <p role="status" className="text-muted-foreground text-sm">
            A request for this selection needs confirmation. Use Retry request
            in the notification before starting another copy.
          </p>
        )}
      </div>
    </AppModal>
  );
}
